/** @jsxImportSource @emotion/react */
import React, { FC, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { generatePath, useNavigate, useParams } from 'react-router-dom';
import { AxiosError } from 'axios';
import { Box, Button, css, Fade } from '@mui/material';
import { MenuOrderItemResponse, MenuType } from 'api/models';
import { ErrorResponseCode } from 'api/models/error-response-code';
import trashBinIcon from 'assets/trashBinIcon.svg';
import { Meal, Meals, useCartContext } from 'components/Cart/cartContext';
import MainButton from 'components/MainButton';
import { errorNotification, successNotification } from 'components/Snackbar';
import { useCheckout } from 'hooks/useCheckout';
import { useDialog } from 'hooks/useDialog';
import { useTelegramUser } from 'hooks/useTelegramUser';
import { isEqual } from 'lodash';
import CheckoutComment from 'pages/Checkout/components/CheckoutComment';
import { useCheckoutComment } from 'pages/Checkout/components/CheckoutComment/useCheckoutComment';
import CheckoutDeleteOrderDialog from 'pages/Checkout/components/CheckoutDeleteOrderDialog';
import CheckoutHeader from 'pages/Checkout/components/CheckoutHeader';
import CheckoutList from 'pages/Checkout/components/CheckoutList';
import { useIsEditable } from 'pages/Checkout/useIsEditable';
import {
  TXT_CLEAR_ALL,
  TXT_ERROR_ORDER_COMMENT_MAX_SIZE,
  TXT_ORDER,
  TXT_ORDER_SUCCESSFULLY_CREATED,
  TXT_ORDER_SUCCESSFULLY_DELETED,
  TXT_ORDER_SUCCESSFULLY_UPDATED,
} from 'translations';
import { ROUTES } from 'utils/routes';
import { sanitize } from 'utils/sanitize';
import { formatCurrency } from 'utils/string/formatCurrency';
import { ErrorWithCode } from 'utils/types/errorTypes';
import { useApi } from './useApi';
import { getDayMeals, getErrorCodeMessage, MAX_COMMENT_SIZE } from './utils';

const Checkout: FC = () => {
  const { t } = useTranslation();
  const { dayDate } = useParams<{ dayDate: string; userId: string }>();
  const navigate = useNavigate();
  const { meals, updateOrder, clearOrder } = useCartContext();
  const { getOrder, submitOrder, deleteOrder } = useApi();
  const { totalPrice } = useCheckout(meals, dayDate as string);
  const { openDialog, handleOpenDialog, handleCloseDialog } = useDialog();
  const { id: userId } = useTelegramUser();

  const order = getOrder.data?.data;
  const isEditable = useIsEditable(getOrder);
  const { orderComment, setOrderComment, checkoutCommentMethods } =
    useCheckoutComment({ comment: order?.comment });

  const handleSuccess = (message: string) => {
    successNotification(t(message));
    navigate(generatePath(ROUTES.WEEK, { userId: `${userId}` }));
    getOrder.remove();
  };

  const handleClearOrder = () => {
    if (order?.id) {
      deleteOrder.mutate(
        { orderId: order.id },
        {
          onSuccess: () => {
            clearOrder();
            handleSuccess(TXT_ORDER_SUCCESSFULLY_DELETED);
          },
        },
      );
    } else {
      clearOrder();
      navigate(generatePath(ROUTES.WEEK, { userId: `${userId}` }));
    }
  };

  const handleCheckout = () => {
    if (orderComment.length > MAX_COMMENT_SIZE) {
      errorNotification(
        t(TXT_ERROR_ORDER_COMMENT_MAX_SIZE, { count: MAX_COMMENT_SIZE }),
      );
      checkoutCommentMethods.current?.focus();
      return;
    }

    const items = getDayMeals(meals, dayDate).map((meal) => ({
      menuItemId: meal.item.id!,
      quantity: meal.quantity,
    }));

    const comment = sanitize(orderComment) || undefined;

    submitOrder.mutate(
      { items, comment, existingOrderId: order?.id },
      {
        onSuccess: () => {
          handleSuccess(
            order?.id
              ? TXT_ORDER_SUCCESSFULLY_UPDATED
              : TXT_ORDER_SUCCESSFULLY_CREATED,
          );
        },
        onError: (error) => {
          const code = (error as AxiosError<ErrorWithCode>)?.response?.data
            ?.code as ErrorResponseCode;

          errorNotification(t(getErrorCodeMessage({ code })));
        },
      },
    );
  };

  const items = getDayMeals(meals, dayDate);

  useEffect(() => {
    if (!getOrder.isFetched) {
      // no up-to-date order to compare
      return;
    }
    if (items.length) {
      // don't override existing items
      // they will be updated with handleClearOrder / handleCheckout if necessary
      return;
    }

    if (!order?.id) {
      navigate(
        generatePath(ROUTES.MENU_CURRENT_DAY, {
          dayDate,
          userId: `${userId}`,
        }),
      );
    } else {
      const orderMeals: Meals = {};
      order.orderItems?.forEach((item) => {
        if (item.menuItem?.id) {
          orderMeals[item.menuItem.id] = {
            item: item.menuItem,
            quantity: item.quantity,
            day: dayDate as string,
            menuType: order.menuType as MenuType,
          };
        }
      });

      // Object.values(meals).forEach((meal) => {
      //   orderMeals[meal.item.id] = {
      //     item: meal.item,
      //     quantity: meal.quantity,
      //   };
      // });
      //
      // if (!isEqual(orderMeals, meals)) {
      updateOrder(orderMeals);
      // }
    }
  }, [getOrder.isFetched, items]);

  const compareOrderWithMeals = (
    items: Meal[],
    orderItems?: MenuOrderItemResponse[],
  ) => {
    if (!orderItems) {
      return false;
    }

    // Compare by (menuItemId, quantity) only: the next-stack MenuOrderItemResponse carries server
    // fields the cart never has (id, price, note, components…), so a deep compare of the whole DTO
    // would always differ and keep the submit button permanently visible. Sorted, because the cart
    // is an object map and its iteration order need not match the server's array order.
    const key = (menuItemId?: number, quantity?: number) =>
      `${menuItemId}:${quantity}`;

    const cartKeys = items.map((i) => key(i.item.id, i.quantity)).sort();
    const orderKeys = orderItems
      .map((i) => key(i.menuItem?.id, i.quantity))
      .sort();

    return isEqual(cartKeys, orderKeys);
  };

  const orderItems = getOrder.data?.data.orderItems;
  const orderedMealEqual = compareOrderWithMeals(items, orderItems);
  const commentChanged = orderComment !== (order?.comment || '');
  const isButtonVisible =
    totalPrice !== 0 && (!orderItems || !orderedMealEqual || commentChanged);
  const isLoading = submitOrder.isLoading;

  return (
    <Fade in={getOrder.isSuccess || !!items.length}>
      <Box>
        <CheckoutHeader isEditable={isEditable} />

        <CheckoutList items={items} dayDate={dayDate} />

        {isEditable && (
          <Button
            variant="outlined"
            size="large"
            onClick={handleOpenDialog}
            endIcon={<img src={trashBinIcon} alt="trash bin" />}
            fullWidth
            disabled={deleteOrder.isLoading}
            css={css`
              padding-top: 14px;
              padding-bottom: 14px;
              margin-top: 32px;
            `}
          >
            {t(TXT_CLEAR_ALL)}
          </Button>
        )}

        <Box
          css={css`
            margin-top: 32px;
            margin-bottom: 32px;
          `}
        >
          <CheckoutComment
            value={orderComment}
            onChange={setOrderComment}
            disabled={!isEditable}
            methods={checkoutCommentMethods}
          />
        </Box>

        <CheckoutDeleteOrderDialog
          open={openDialog}
          onClose={handleCloseDialog}
          onConfirm={handleClearOrder}
          isLoading={deleteOrder.isLoading}
        />

        <MainButton
          visible={isButtonVisible}
          disabled={isLoading || !isButtonVisible}
          loading={isLoading}
          onClick={handleCheckout}
        >
          {t(TXT_ORDER)} ({formatCurrency(totalPrice)})
        </MainButton>
      </Box>
    </Fade>
  );
};

export default Checkout;
